/**
 * WBSearch - our own product search for the header search (snippets/header-search.liquid).
 * No external search service: the product index comes from the theme
 * (templates/collection.search-index.liquid, loaded once on first use from
 * shop-sportswear and shop) and is
 * searched here in the browser.
 *
 * Matching: every query word has to match the product (title, product type or
 * tag). A word matches exactly, as a prefix ("legg" -> leggings, so results
 * show while typing), as its singular ("bras" -> bra) or with one typo
 * (two for words of 8+ letters: "legings" -> leggings). Ranking: title matches
 * before type before tag matches, exact before prefix before typo; then products
 * in stock first, then the highest stock of a single variant (as Algolia ranked
 * ME), then the collection order.
 *
 * WBSearch.search(query) resolves to [{handle, available}] (best first);
 * an empty array when nothing matches or the index can't be loaded.
 */
(function () {
  'use strict';

  var LABEL = '[WBSearch]';
  // Tags that are on (almost) every product or are internal markers - they would
  // make every product match. Everything with ":" or "_" is already left out in Liquid.
  var IGNORED_TAG = /^(new|instock|outlet|discontinued|sportswear|color-sorting|best-seller.*|bestseller.*|launch.*|deal.*|dotd.*|push.*|hoverimg.*|stylecombo.*|colour\d*|cart-cab|index-reco|winter-sale|spring-edit-pick|middle-east-import|algolia.*|ems.*|\d.*|.*\d{4,}.*)$/i;
  var WEIGHT = { title: 10, type: 6, tag: 4 };
  var KIND = { exact: 1, prefix: 0.8, singular: 0.9, typo: 0.5 };

  var indexPromise = null;

  function normalize(text) {
    return String(text || '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[ً-ْـ]/g, '') // Arabic diacritics + tatweel
      .replace(/[^0-9a-z؀-ۿ]+/g, ' ')
      .trim();
  }

  function words(text) {
    var normalized = normalize(text);
    return normalized ? normalized.split(' ') : [];
  }

  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // The collections the index is built from, in this order (search.search.liquid
  // renders cards for products of these collections).
  var INDEX_COLLECTIONS = ['shop-sportswear', 'shop'];

  // No Accept: application/json header - with it Shopify answers with its own
  // collection JSON and ignores the view.
  function fetchIndexPage(handle, page) {
    return fetch(rootPath() + 'collections/' + handle + '?view=search-index&page=' + page)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status + ' ' + handle);
        return response.json();
      });
  }

  // All pages of one collection: page 1 tells how many there are, the rest load in parallel.
  function fetchCollection(handle) {
    return fetchIndexPage(handle, 1).then(function (first) {
      var rest = [];
      for (var page = 2; page <= (first.pages || 1); page++) {
        rest.push(fetchIndexPage(handle, page));
      }
      return Promise.all(rest).then(function (pages) {
        return pages.reduce(function (all, data) { return all.concat(data.products || []); }, first.products || []);
      });
    });
  }

  // The index, loaded once per page (the browser/Shopify cache it between pages).
  function loadIndex() {
    if (!indexPromise) {
      indexPromise = Promise.all(INDEX_COLLECTIONS.map(fetchCollection))
        .then(function (lists) {
          var seen = {};
          return lists.reduce(function (all, list) { return all.concat(list); }, []).filter(function (product) {
            if (seen[product.h]) return false;
            seen[product.h] = true;
            return true;
          });
        })
        .then(function (products) {
          return products.map(function (product, order) {
            var type = String(product.y || '').split('/').join(' ');
            var tags = String(product.g || '').split(' ').filter(function (tag) {
              return tag && !IGNORED_TAG.test(tag);
            }).join(' ');
            return {
              handle: product.h,
              available: !!product.a,
              stock: Number(product.s) || 0,
              order: order,
              fields: { title: words(product.t), type: words(type), tag: words(tags) }
            };
          });
        })
        .catch(function (error) {
          console.log(LABEL, 'index', error);
          indexPromise = null;
          return [];
        });
    }
    return indexPromise;
  }

  // Levenshtein distance, stopping early once it exceeds max.
  function withinDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return false;
    var previous = [];
    var current;
    for (var j = 0; j <= b.length; j++) previous[j] = j;
    for (var i = 1; i <= a.length; i++) {
      current = [i];
      var rowMin = i;
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        current[k] = Math.min(previous[k] + 1, current[k - 1] + 1, previous[k - 1] + cost);
        if (current[k] < rowMin) rowMin = current[k];
      }
      if (rowMin > max) return false;
      previous = current;
    }
    return previous[b.length] <= max;
  }

  function singular(word) {
    return word.length > 3 && word.charAt(word.length - 1) === 's' ? word.slice(0, -1) : word;
  }

  // Best score of one query word against one field's words (0 = no match).
  function matchWord(token, fieldWords, isLast) {
    var best = 0;
    var maxTypos = token.length >= 8 ? 2 : (token.length >= 4 ? 1 : 0);
    for (var i = 0; i < fieldWords.length; i++) {
      var word = fieldWords[i];
      var score = 0;
      if (word === token) {
        score = KIND.exact;
      } else if (singular(word) === singular(token)) {
        score = KIND.singular;
      } else if (word.indexOf(token) === 0 && (isLast || token.length >= 3)) {
        // Prefixes count for the word being typed, and for finished words of 3+ letters.
        score = KIND.prefix;
      } else if (maxTypos && withinDistance(token, word.slice(0, token.length + maxTypos), maxTypos) && withinDistance(token, word, maxTypos)) {
        score = KIND.typo;
      }
      if (score > best) best = score;
      if (best === KIND.exact) break;
    }
    return best;
  }

  function scoreProduct(product, tokens) {
    var total = 0;
    for (var i = 0; i < tokens.length; i++) {
      var isLast = i === tokens.length - 1;
      var best = 0;
      for (var field in WEIGHT) {
        var score = matchWord(tokens[i], product.fields[field], isLast) * WEIGHT[field];
        if (score > best) best = score;
      }
      if (!best) return 0; // every query word has to match
      total += best;
    }
    return total;
  }

  function search(query) {
    var tokens = words(query);
    if (!tokens.length) return Promise.resolve([]);
    return loadIndex().then(function (index) {
      var hits = [];
      index.forEach(function (product) {
        var score = scoreProduct(product, tokens);
        if (score) hits.push({ product: product, score: score });
      });
      hits.sort(function (a, b) {
        return (b.score - a.score) ||
          (Number(b.product.available) - Number(a.product.available)) ||
          (b.product.stock - a.product.stock) ||
          (a.product.order - b.product.order);
      });
      return hits.map(function (hit) {
        return { handle: hit.product.handle, available: hit.product.available };
      });
    });
  }

  window.WBSearch = {
    load: loadIndex,
    search: search
  };
})();
